<template>
  <div ref="mapContainer" class="grondverzet-map"></div>
</template>

<script>
import Feature from 'ol/Feature'
import OlMap from 'ol/Map'
import OlView from 'ol/View'
import _ from 'lodash'
import GeoJSON from 'ol/format/GeoJSON'
import MultiPoint from 'ol/geom/MultiPoint'
import MultiPolygon from 'ol/geom/MultiPolygon'
import Draw from 'ol/interaction/Draw'
import Modify from 'ol/interaction/Modify'
import Snap from 'ol/interaction/Snap'
import { unByKey } from 'ol/Observable'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import { fromLonLat } from 'ol/proj'
import OSM from 'ol/source/OSM'
import TileWMS from 'ol/source/TileWMS'
import VectorSource from 'ol/source/Vector'
import CircleStyle from 'ol/style/Circle'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import { OVAM_EPSG, readProjectzoneGeometry, writeProjectGeometry } from '@/utils/grondverzet/grondverzetMapping'
import { addParcel, removeParcel } from '@/utils/grondverzet/parcelZone'
import { zoomToAddress } from '@/utils/addressSearch/zoomToAddress'

const MAP_PROJECTION = 'EPSG:3857'
// A single small parcel would otherwise be fitted to a zoom level with no surroundings left to
// recognize the location by.
const MAX_FIT_ZOOM = 18
const FIT_PADDING = [16, 16, 16, 16]
// Fallback view for a project with no contour, no address that can be found and no location point: Flanders, the only area
// where an OVAM projectzone can lie.
const FLANDERS_CENTER = [4.4, 51.0]
const FLANDERS_ZOOM = 8
const PROJECT_POINT_ZOOM = 14

// Flemish large-scale reference map (GRB): parcels and buildings, drawn over OSM. It is the map an
// OVAM projectzone is judged against, so it is always on - but only from the zoom where parcels mean
// something: at overview zoom the service needs seconds per tile while OSM answers instantly. The
// extent is Flanders, so no requests go out for the rest of Europe.
const GRB_URL = 'https://geo.api.vlaanderen.be/GRB-basiskaart/wms'
const GRB_LAYER = 'GRB_BSK'
const GRB_EXTENT = [272733, 6559615, 673483, 6719165]
const GRB_MIN_ZOOM = 11
const GRB_ATTRIBUTION = '&copy; <a href="https://www.vlaanderen.be/digitaal-vlaanderen" target="_blank" rel="noopener noreferrer">Digitaal Vlaanderen</a> GRB'

// Picking a parcel asks the GRB administratieve percelen layer what lies under the click. This goes
// through WMS GetFeatureInfo rather than the WFS on the same data: both answer with the identical
// polygon, but GetFeatureInfo does it in ~0.2 s where the WFS takes 8-12 s.
const PARCEL_URL = 'https://geo.api.vlaanderen.be/GRB/wms'
const PARCEL_LAYER = 'GRB_ADP'
// Hovering asks the same service, so it waits for the pointer to settle - and only asks at all once
// the pointer has left the parcel it already knows.
const PARCEL_HOVER_DELAY = 250

const PARCEL_HOVER_STYLE = new Style({
  fill: new Fill({ color: 'rgba(21, 112, 239, 0.10)' }),
  stroke: new Stroke({ color: '#1570ef', width: 2, lineDash: [5, 4] })
})

// Address/place search: the same service the project map uses (see mapHandlers.search), limited to
// Belgium because an OVAM projectzone has to lie there. Zoom stops well above street level, so the
// zone can be drawn right after searching.
const SEARCH_URL = 'https://nominatim.openstreetmap.org/search'
const SEARCH_ZOOM = 17

const ZONE_COLOR = '#679e37'

/**
 * The polygons of a (multi)polygon as plain MultiPolygon coordinates, flattened to 2D.
 * @param {import('ol/geom/Geometry').default} geometry
 * @returns {number[][][][]}
 */
function polygonsOf(geometry) {
  if (!geometry) {
    return []
  }
  const type = geometry.getType()
  const polygons = type === 'MultiPolygon' ? geometry.getCoordinates() : type === 'Polygon' ? [geometry.getCoordinates()] : []
  return polygons.map(polygon => polygon.map(ring => ring.map(([x, y]) => [x, y])))
}

/**
 * All distinct corner points of a (multi)polygon, so they can be drawn as grab handles. The closing
 * coordinate of a ring repeats the first one and is skipped.
 * @param {import('ol/geom/Geometry').default} geometry
 * @returns {number[][]}
 */
function cornerPoints(geometry) {
  if (!geometry || typeof geometry.getCoordinates !== 'function') {
    return []
  }
  const rings = geometry.getType() === 'MultiPolygon'
    ? geometry.getCoordinates().reduce((all, polygon) => all.concat(polygon), [])
    : geometry.getCoordinates()
  return rings.reduce((points, ring) => points.concat(ring.slice(0, -1)), [])
}

// The zone plus a handle on every corner: visible handles are what tells the user the shape can be
// reshaped by dragging, which is why there is no separate edit mode.
const ZONE_STYLE = [
  new Style({
    fill: new Fill({ color: 'rgba(123, 199, 66, 0.25)' }),
    stroke: new Stroke({ color: ZONE_COLOR, width: 2 })
  }),
  new Style({
    image: new CircleStyle({
      radius: 4.5,
      fill: new Fill({ color: '#fff' }),
      stroke: new Stroke({ color: ZONE_COLOR, width: 2 })
    }),
    geometry: (feature) => new MultiPoint(cornerPoints(feature.getGeometry()))
  })
]

/**
 * Map showing the OVAM projectzone geometry ("SRID=3812;<WKT>") on an OSM background. The zone is
 * always editable by dragging its corner handles (reported through `modified` on every change, so
 * the parent decides when to keep it); mode 'draw' replaces it with a freshly drawn one (`drawn`);
 * mode 'parcel' adds a clicked parcel to it, or cuts it out when the click lies inside the zone (`parcel`).
 * Both report project contour WKT (EPSG:4326).
 */
export default {
  name: 'GrondverzetGeometryMap',
  props: {
    geometry: {
      type: String,
      default: ''
    },
    mode: {
      type: String,
      default: '',
      validator: (value) => ['', 'draw', 'parcel'].includes(value)
    },
    // Project location point as [lon, lat]; only used to center the map when there is no zone yet.
    center: {
      type: Array,
      default: null
    },
    // Project address as search text; without a zone the map zooms to it, which beats the point.
    address: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      map: null,
      source: null,
      interactions: [],
      // Only used to build GetFeatureInfo urls for the parcel layer; it is never rendered.
      parcelSource: null,
      parcelClickKey: null,
      parcelMoveKey: null,
      // Outline of the parcel under the pointer, so the user sees what a click would take.
      hoverSource: null,
      hoverGeometry: null,
      // Guards against a slow answer overwriting the parcel the pointer has moved on to.
      hoverRequest: 0,
      resizeObserver: null,
      hasFitted: false
    }
  },
  watch: {
    geometry() {
      this.renderGeometry()
    },
    mode(mode) {
      this.setMode(mode)
    }
  },
  mounted() {
    this.source = new VectorSource()
    this.hoverSource = new VectorSource()
    this.parcelSource = new TileWMS({ url: PARCEL_URL, params: { LAYERS: PARCEL_LAYER } })
    this.hoverParcel = _.debounce(this.hoverParcel, PARCEL_HOVER_DELAY)
    this.map = new OlMap({
      target: this.$refs.mapContainer,
      layers: [
        new TileLayer({ source: new OSM() }),
        new TileLayer({
          source: new TileWMS({
            url: GRB_URL,
            params: { LAYERS: GRB_LAYER, TRANSPARENT: true },
            attributions: GRB_ATTRIBUTION,
            crossOrigin: 'anonymous'
          }),
          extent: GRB_EXTENT,
          minZoom: GRB_MIN_ZOOM
        }),
        new VectorLayer({ source: this.hoverSource, style: PARCEL_HOVER_STYLE }),
        new VectorLayer({ source: this.source, style: ZONE_STYLE })
      ],
      view: new OlView({
        projection: MAP_PROJECTION,
        center: fromLonLat(this.center || FLANDERS_CENTER),
        zoom: this.center ? PROJECT_POINT_ZOOM : FLANDERS_ZOOM
      })
    })
    this.renderGeometry()
    this.zoomToProjectAddress()
    this.setMode(this.mode)
    // The map fills its pane, so its size follows the popup layout (window resize, or the columns
    // stacking on a narrow screen). OpenLayers does not notice that by itself.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.map) {
          this.map.updateSize()
        }
      })
      this.resizeObserver.observe(this.$refs.mapContainer)
    }
  },
  methods: {
    renderGeometry() {
      if (!this.map) {
        return
      }
      this.source.clear()
      const geometry = readProjectzoneGeometry(this.geometry)
      if (!geometry) {
        return
      }
      geometry.transform(OVAM_EPSG, MAP_PROJECTION)
      this.source.addFeature(new Feature({ geometry }))
      // The popup fades in, so on the first render the container can still be sizing up: without
      // updateSize the view would be fitted to a 0x0 viewport and the zone ends up off-screen.
      this.$nextTick(() => {
        if (!this.map) {
          return
        }
        this.map.updateSize()
        // Only the first zone shown is fitted; after that the view is the user's (they just drew or
        // reshaped the zone, or panned to it), so re-rendering must not move it.
        if (!this.hasFitted) {
          this.map.getView().fit(geometry.getExtent(), { padding: FIT_PADDING, maxZoom: MAX_FIT_ZOOM })
          this.hasFitted = true
        }
      })
    },
    /* Without a zone, moves the view to the project address once the address has been located. */
    zoomToProjectAddress() {
      if (!this.address || this.zoneGeometry()) {
        return
      }
      // A zone drawn while the address was being looked up is what the user is looking at.
      zoomToAddress(this.map.getView(), this.address, () => !!this.map && !this.zoneGeometry())
    },
    /* Drops an unsaved reshape and shows the zone as it is stored on the project again. */
    resetGeometry() {
      this.renderGeometry()
    },
    /**
     * Moves the view to the first hit for an address or place name.
     * @param {string} query
     * @returns {Promise<string>} the name of what was found, or '' when the search yielded nothing.
     * The name matters: with the search limited to Belgium, a query for somewhere else returns the
     * closest Belgian match instead of nothing, so the caller has to be able to show what it hit.
     */
    async searchPlace(query) {
      const term = String(query || '').trim()
      if (!term || !this.map) {
        return ''
      }
      // Without accept-language the service answers in whatever the browser asks for, which is how
      // a Dutch user ended up with "Antwerp, Flanders, Belgium"; the logged-in language decides.
      const language = (this.$i18n && this.$i18n.locale) || ''
      let hits = []
      try {
        const response = await fetch(
          `${SEARCH_URL}?q=${encodeURIComponent(term)}&format=jsonv2&countrycodes=be&limit=1` +
          (language ? `&accept-language=${encodeURIComponent(language)}` : '')
        )
        if (!response.ok) {
          return ''
        }
        hits = await response.json()
      } catch (error) {
        return ''
      }
      if (!Array.isArray(hits) || !hits.length || !this.map) {
        return ''
      }
      this.map.getView().animate({
        center: fromLonLat([Number(hits[0].lon), Number(hits[0].lat)]),
        zoom: SEARCH_ZOOM,
        duration: 600
      })
      return hits[0].display_name || term
    },
    setMode(mode) {
      if (!this.map) {
        return
      }
      this.interactions.forEach((interaction) => this.map.removeInteraction(interaction))
      this.interactions = []

      this.stopHovering()

      if (mode === 'draw') {
        const draw = new Draw({ source: this.source, type: 'Polygon' })
        // A project has one contour, so a new zone replaces whatever is drawn.
        draw.on('drawstart', () => this.source.clear())
        draw.on('drawend', (event) => this.$emit('drawn', this.contourFrom(event.feature)))
        this.interactions = [draw]
      } else if (mode === 'parcel') {
        // No interaction: picking is a plain click, and Modify stays off so a click on a parcel
        // inside the zone (to remove it) cannot start dragging a corner instead.
        this.parcelClickKey = this.map.on('singleclick', (event) => this.pickParcel(event.coordinate))
        this.parcelMoveKey = this.map.on('pointermove', (event) => {
          if (!event.dragging) {
            this.hoverParcel(event.coordinate)
          }
        })
        this.setCursor('crosshair')
      } else {
        // Reshaping needs no mode: the handles are always grabbable. Snapping to the zone itself
        // makes it easier to drop a corner back onto an existing edge.
        const modify = new Modify({ source: this.source })
        modify.on('modifyend', (event) => this.$emit('modified', this.contourFrom(event.features.item(0))))
        this.interactions = [modify, new Snap({ source: this.source })]
      }

      this.interactions.forEach((interaction) => this.map.addInteraction(interaction))
    },
    zoneGeometry() {
      const feature = this.source && this.source.getFeatures()[0]
      return feature ? feature.getGeometry() : null
    },
    isInZone(coordinate) {
      const zone = this.zoneGeometry()
      return !!zone && zone.intersectsCoordinate(coordinate)
    },
    contourFrom(feature) {
      return feature ? writeProjectGeometry(feature.getGeometry(), MAP_PROJECTION) : ''
    },
    setCursor(cursor) {
      const element = this.map && this.map.getTargetElement()
      if (element) {
        element.style.cursor = cursor
      }
    },
    /* Ends parcel mode: no more lookups, no outline, normal cursor. */
    stopHovering() {
      if (this.parcelClickKey) {
        unByKey(this.parcelClickKey)
        this.parcelClickKey = null
      }
      if (this.parcelMoveKey) {
        unByKey(this.parcelMoveKey)
        this.parcelMoveKey = null
      }
      if (this.hoverParcel.cancel) {
        this.hoverParcel.cancel()
      }
      this.hoverRequest += 1
      this.hoverGeometry = null
      if (this.hoverSource) {
        this.hoverSource.clear()
      }
      this.setCursor('')
      this.$emit('parcel-hover', '')
    },
    /**
     * Outlines the parcel under the pointer and reports its CAPAKEY through `parcel-hover`, plus
     * whether it lies in the zone already (a click would then remove it).
     * Debounced, and skipped entirely while the pointer stays inside the parcel already outlined.
     * @param {number[]} coordinate
     */
    async hoverParcel(coordinate) {
      if (!this.map || this.mode !== 'parcel') {
        return
      }
      if (this.hoverGeometry && this.hoverGeometry.intersectsCoordinate(coordinate)) {
        return
      }
      const request = ++this.hoverRequest
      const parcel = await this.fetchParcel(coordinate)
      // The pointer moved on (or the mode ended) while this answer was on its way.
      if (request !== this.hoverRequest || !this.map || this.mode !== 'parcel') {
        return
      }
      this.hoverSource.clear()
      this.hoverGeometry = parcel ? parcel.feature.getGeometry() : null
      if (parcel) {
        this.hoverSource.addFeature(parcel.feature)
      }
      this.$emit('parcel-hover', parcel ? parcel.capakey : '', this.isInZone(coordinate))
    },
    /**
     * The GRB administratief perceel under a coordinate, or null when there is none.
     * @param {number[]} coordinate
     * @returns {Promise<{feature: import('ol/Feature').default, capakey: string}|null>}
     */
    async fetchParcel(coordinate) {
      const view = this.map.getView()
      const url = this.parcelSource.getFeatureInfoUrl(coordinate, view.getResolution(), view.getProjection(), {
        INFO_FORMAT: 'application/json',
        FEATURE_COUNT: 1
      })
      if (!url) {
        return null
      }
      try {
        const response = await fetch(url)
        if (!response.ok) {
          return null
        }
        // The service answers in the projection the url asked for, which is the view's.
        const features = new GeoJSON().readFeatures(await response.json())
        return features.length ? { feature: features[0], capakey: features[0].get('CAPAKEY') || '' } : null
      } catch (error) {
        return null
      }
    },
    /**
     * Asks GRB which administratief perceel lies under the clicked coordinate and adds it to the
     * zone, or cuts it out when the click lies inside the zone. Adjacent parcels merge into one
     * polygon, separate ones stay separate polygons of the zone. Emits `parcel` with
     * { contour, capakey, removed } (contour '' when nothing is left), or null when there is no
     * parcel there (outside Flanders, on water, on a road).
     * @param {number[]} coordinate map coordinate of the click
     */
    async pickParcel(coordinate) {
      if (!this.map) {
        return
      }
      this.$emit('parcel-loading')
      const parcel = await this.fetchParcel(coordinate)
      if (!parcel || !this.map) {
        this.$emit('parcel', null)
        return
      }
      const removed = this.isInZone(coordinate)
      const combine = removed ? removeParcel : addParcel
      const polygons = combine(polygonsOf(this.zoneGeometry()), polygonsOf(parcel.feature.getGeometry()))
      this.$emit('parcel', {
        contour: polygons.length ? writeProjectGeometry(new MultiPolygon(polygons), MAP_PROJECTION) : '',
        capakey: parcel.capakey,
        removed
      })
    }
  },
  beforeDestroy() {
    this.stopHovering()
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.map) {
      this.map.setTarget(undefined)
      this.map = null
    }
  }
}
</script>

<style lang="less">
@import '../openLayers/ol-custom.less';
</style>

<style lang="less" scoped>
// Fills the pane it is placed in; the popup decides how tall that pane is.
.grondverzet-map {
  width: 100%;
  height: 100%;
  background-color: #e8e8e8;
}
</style>
